'use strict';
/* ==========================================================================
   core-local/fair.js — "It's Only a Theory?!": the lesson's look (DESIGN.md §2). Energy's engraving collage on
   BLUEPRINT paper, a school-gym science fair, and a lab-notebook twist: a graph-paper inset for the Kepler plots.
   Law cards are BLUE (blueprint) stamped WHAT; theory cards are BRASS / AMBER stamped WHY.
   Drawn through the collage engine (engine.js globals). Every piece is a cached shape (paperRect / paperCircle with
   fixed seeds and whole-px sizes, baked once into sprites); animation is by transform (translate / rotate / scale:
   the `s` option), never a new shape per frame, and no shadowOn() (paper brings its own pre-baked shadow).

   Fair.C                              colors: blue / blueDeep / grid (blueprint), law / lawDeep (blue cards), amber /
                                       amberDeep (theory cards), hypo (hypothesis cards), kraft, cream, paper, ink, red
                                       (Voss's grease pencil), table (the gym table), graph (graph-paper cream)
   Fair.backdrop()                     the blueprint wall: IMG.tex_blueprint, else navy paper + a drawn grid (full frame)
   Fair.floor(y = 930)                 the gym floor: a wide kraft plank strip from y down, with board lines
   Fair.table(x, y, w, o)              a science-fair table (kraft top + a cream cloth skirt) {seed, s} → the top's y
   Fair.card(kind, x, y, w, h, o)      a law / theory / hypothesis index card: kind 'law' (blue, stamp WHAT) | 'theory'
                                       (amber, WHY) | 'hypothesis' (cream, TEST IT) | 'plain'; o = {title, body, stamp:
                                       true | text, seed, rot, s, t0 (pops in from t0), alpha, flip (0..1: a card flip
                                       by x-scale), hi ('ok' | 'bad' | null: a green tick ring / a red cross)}
   Fair.board(x, y, w, h, o)           a TRI-FOLD science-fair poster (IMG.science_fair_board when loaded and o.art, else
                                       paper panels): {title, seed, s, rot, art} → {x, y, w, h, inner: {x, y, w, h}} (the
                                       middle panel's writable area, for claims)
   Fair.graph(x, y, w, h, o)           a graph-paper inset with axes, tick labels, points and an optional fitted line:
                                       o = {xmax, ymax, xLabel, yLabel, xticks: [..], yticks: [..], points: [{x, y,
                                       label, hi}], line: fn(x) → y | null, prog (0..1: how much of the line is drawn),
                                       pointsT0 (points pop in from here, 0.12 s apart), seed, rot, title} → {X(x), Y(y)}
                                       (data → screen, for callouts). Pure in T.
   Fair.pencil(pts, o)                 a red grease-pencil stroke {prog, seed, w}; Fair.strike(x, y, w, o) strikes a line
                                       of text through; Fair.ring(x, y, rx, ry, o) circles it
   Fair.stampWord(word, x, y, o)       the round TESTED / WHAT / WHY rubber stamp {s, rot, color}
   Fair.fitFont(text, maxW, max, family)  sets g.font to the largest size ≤ max that fits maxW (measured) → the size
   ========================================================================== */
(function () {
  const C = {
    blue: '#2f5f8f', blueDeep: '#23476d', grid: 'rgba(220,235,255,0.18)',
    law: '#3f78b5', lawDeep: '#244f80', lawInk: '#fbf5e6',
    amber: '#e0a33a', amberDeep: '#9a6512', amberInk: '#2b2420',
    hypo: '#f3ead7', hypoInk: '#253c5a',
    kraft: PAL.kraft, cream: PAL.cream, paper: PAL.paper, ink: PAL.ink, red: '#c8321f', navy: PAL.navy,
    table: '#b98452', tableDark: '#8f623a', cloth: '#f6efdc', graph: '#f7f4ea', graphLine: 'rgba(90,150,190,0.35)', graphBold: 'rgba(90,150,190,0.6)',
  };
  const R = Math.round;
  /* set g.font to `family` at the largest size ≤ max whose `text` fits maxW (measured, not guessed) → the size */
  function fitFont(text, maxW, max, family, min = 16) {
    g.font = `${max}px ${family}`;
    const w = g.measureText(String(text)).width, size = w > maxW ? Math.max(min, Math.floor(max * maxW / w)) : max;
    g.font = `${size}px ${family}`;
    return size;
  }

  function backdrop() {
    if (IMG.tex_blueprint) { g.drawImage(IMG.tex_blueprint, 0, 0, Game.W, Game.H); return; }
    g.fillStyle = paperTile(C.blue, 7); g.fillRect(0, 0, Game.W, Game.H);
    g.save(); g.strokeStyle = 'rgba(220,235,255,0.16)'; g.lineWidth = 1.2; g.beginPath();
    for (let x = 0; x <= Game.W; x += 48) { g.moveTo(x, 0); g.lineTo(x, Game.H); }
    for (let y = 0; y <= Game.H; y += 48) { g.moveTo(0, y); g.lineTo(Game.W, y); }
    g.stroke(); g.lineWidth = 2; g.strokeStyle = 'rgba(220,235,255,0.22)'; g.beginPath();
    for (let x = 0; x <= Game.W; x += 240) { g.moveTo(x, 0); g.lineTo(x, Game.H); }
    for (let y = 0; y <= Game.H; y += 240) { g.moveTo(0, y); g.lineTo(Game.W, y); }
    g.stroke(); g.restore();
  }
  function floor(y = 930) {
    const h = Game.H - y + 40;
    paperRect(960, R(y + h / 2), 2000, R(h), C.kraft, { seed: 12, torn: true, depth: 1.4, tex: 'tex_kraft', boil: false });
    for (let i = 0; i < 3; i++) ink([[0, y + 40 + i * 44], [1920, y + 42 + i * 44]], { w: 2, color: 'rgba(90,60,30,0.25)', seed: 14 + i, passes: 1, step: 90, amp: 0.8 });
  }
  function table(x, y, w, { seed = 21, s = 1 } = {}) {
    if (s <= 0) return y;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, 70, R(w - 30), 140, C.cloth, { seed: seed + 1, depth: 1, boil: false });
    for (let k = -3; k <= 3; k++) ink([[k * (w / 8), 8], [k * (w / 8) + 6, 136]], { w: 2, color: 'rgba(120,100,70,0.25)', seed: seed + 10 + k, passes: 1, step: 40 });
    paperRect(0, 0, R(w), 26, C.table, { seed, depth: 1.4, boil: false, tex: 'tex_kraft' });
    g.restore();
    return y - 13 * s;
  }

  const KIND = {
    law: { fill: () => C.law, band: () => C.lawDeep, ink: () => C.lawInk, stamp: 'WHAT', stampColor: () => C.cream },
    theory: { fill: () => C.amber, band: () => C.amberDeep, ink: () => C.amberInk, stamp: 'WHY', stampColor: () => C.amberDeep },
    hypothesis: { fill: () => C.hypo, band: () => C.navy, ink: () => C.hypoInk, stamp: 'TEST IT', stampColor: () => C.navy },
    plain: { fill: () => C.cream, band: () => C.kraft, ink: () => C.ink, stamp: null, stampColor: () => C.ink },
  };
  function wrapLines(text, maxW, font) {
    if (!text) return [];
    if (window.UI && UI._wrap) return UI._wrap(text, maxW, font);
    return [String(text)];
  }
  function card(kind, x, y, w, h, { title = '', body = '', stamp = true, seed = 1, rot = 0, s = 1, t0 = -1e6, alpha = 1, flip = 1, hi = null } = {}) {
    const K = KIND[kind] || KIND.plain;
    const sc = s * pop(T, t0, 0.35, 1.8);
    if (sc <= 0) return null;
    w = R(w); h = R(h);
    g.save(); g.globalAlpha *= alpha; g.translate(x, y); g.rotate(rot); g.scale(sc * flip, sc);
    paperRect(0, 0, w, h, K.fill(), { seed, depth: 1.2 });
    paperRect(0, R(-h / 2 + 22), w - 8, 36, K.band(), { seed: seed + 3, depth: 0, boil: false });
    // index-card rules on the body
    for (let ly = -h / 2 + 76; ly < h / 2 - 14; ly += 38) ink([[-w / 2 + 16, ly], [w / 2 - 16, ly]], { w: 1.5, color: kind === 'law' ? 'rgba(220,235,255,0.35)' : 'rgba(90,150,190,0.35)', seed: seed * 7 + ly, passes: 1, step: 80, amp: 0.6 });
    if (title) {
      fitFont(title, w - 28, 30, "'Alfa Slab One'"); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = kind === 'plain' ? C.ink : C.cream;
      g.fillText(title, 0, R(-h / 2 + 24));
    }
    if (body) {
      const size = Math.max(26, Math.min(40, R(h / 6))), lines = wrapLines(body, w - 44, `${size}px 'Caveat Brush'`).slice(0, Math.max(1, Math.floor((h - 90) / (size * 1.05))));
      lines.forEach((l, i) => hand(l, -w / 2 + 22, R(-h / 2 + 64 + size * 0.55 + i * size * 1.05), size, -1e6, { dur: 0.01, align: 'left', color: K.ink(), seed: seed + 20 + i }));
    }
    const word = stamp === true ? K.stamp : stamp || null;
    if (word) stampWord(word, w / 2 - 70, h / 2 - 48, { s: 0.42, rot: -0.18, color: K.stampColor() });
    if (hi === 'ok') inkCircle(0, 0, Math.max(w, h) * 0.55, { w: 7, color: '#3f8f3a', seed: seed + 40, sx: w / Math.max(w, h), sy: h / Math.max(w, h), step: 8 });
    if (hi === 'bad') { ink([[-w * 0.4, -h * 0.35], [w * 0.4, h * 0.35]], { w: 10, color: C.red, seed: seed + 41 }); ink([[w * 0.4, -h * 0.35], [-w * 0.4, h * 0.35]], { w: 10, color: C.red, seed: seed + 42 }); }
    g.restore();
    return { x, y, w: w * sc, h: h * sc };
  }
  function stampWord(word, x, y, { s = 1, rot = -0.15, color = C.red } = {}) {
    if (window.UI && UI.stamp) { UI.stamp(word, x, y, { rot, s, color }); return; }
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s); g.font = "80px 'Rubik Mono One'"; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = color; g.fillText(word, 0, 0); g.restore();
  }

  function board(x, y, w, h, { title = '', seed = 51, s = 1, rot = 0, art = false } = {}) {
    w = R(w); h = R(h);
    const side = R(w * 0.27), mid = w - 2 * side;
    const out = { x, y, w: w * s, h: h * s, inner: { x, y: y + 30 * s, w: (mid - 60) * s, h: (h - 150) * s } };
    if (s <= 0) return out;
    if (art && IMG.science_fair_board) { cut('science_fair_board', x, y, h * 1.1 * s, { rot, seed, depth: 1.2 }); }
    else {
      g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
      // the side flaps, a touch darker (folded toward us), then the middle panel
      paperRect(R(-mid / 2 - side / 2 + 6), 10, side, R(h - 30), '#e8dcc0', { seed: seed + 1, depth: 1 });
      paperRect(R(mid / 2 + side / 2 - 6), 10, side, R(h - 30), '#e8dcc0', { seed: seed + 2, depth: 1 });
      paperRect(0, 0, mid, h, C.cream, { seed, depth: 1.4 });
      ink([[-mid / 2, -h / 2 + 12], [-mid / 2, h / 2 - 12]], { w: 2, color: 'rgba(80,60,40,0.35)', seed: seed + 3, passes: 1 });
      ink([[mid / 2, -h / 2 + 12], [mid / 2, h / 2 - 12]], { w: 2, color: 'rgba(80,60,40,0.35)', seed: seed + 4, passes: 1 });
      g.restore();
    }
    if (title) {
      g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
      paperRect(0, R(-h / 2 + 58), R(mid - 60), 76, C.law, { seed: seed + 5, depth: 0.8, boil: false });
      fitFont(title, mid - 90, 52, "'Alfa Slab One'"); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.cream;
      g.fillText(title, 0, R(-h / 2 + 60));
      g.restore();
    }
    return out;
  }

  function graph(x, y, w, h, { xmax = 1, ymax = 1, xLabel = '', yLabel = '', xticks = [], yticks = [], points = [], line = null, prog = 1, pointsT0 = -1e6, seed = 61, rot = 0, title = '' } = {}) {
    w = R(w); h = R(h);
    const padL = 90, padB = 70, padT = title ? 64 : 30, padR = 30;
    const x0 = -w / 2 + padL, x1 = w / 2 - padR, y0 = h / 2 - padB, y1 = -h / 2 + padT;
    const X = v => x0 + (v / xmax) * (x1 - x0), Y = v => y0 - (v / ymax) * (y0 - y1);
    g.save(); g.translate(x, y); g.rotate(rot);
    if (IMG.tex_graph) texScrap('tex_graph', 0, 0, w, h, { seed, depth: 1.2, torn: false });
    else {
      paperRect(0, 0, w, h, C.graph, { seed, depth: 1.2 });
      g.save(); g.strokeStyle = C.graphLine; g.lineWidth = 1; g.beginPath();
      for (let gx = -w / 2 + 12; gx < w / 2; gx += 24) { g.moveTo(gx, -h / 2 + 6); g.lineTo(gx, h / 2 - 6); }
      for (let gy = -h / 2 + 12; gy < h / 2; gy += 24) { g.moveTo(-w / 2 + 6, gy); g.lineTo(w / 2 - 6, gy); }
      g.stroke(); g.restore();
    }
    if (title) hand(title, 0, -h / 2 + 34, 36, -1e6, { dur: 0.01, color: C.navy, seed: seed + 1 });
    // axes
    ink([[x0, y0], [x1 + 6, y0]], { w: 3.5, color: C.ink, seed: seed + 2, passes: 1, step: 60 });
    ink([[x0, y0], [x0, y1 - 6]], { w: 3.5, color: C.ink, seed: seed + 3, passes: 1, step: 60 });
    g.font = "22px 'IBM Plex Mono'"; g.fillStyle = C.ink; g.textBaseline = 'top'; g.textAlign = 'center';
    xticks.forEach(v => { g.fillText(String(v), X(v), y0 + 8); ink([[X(v), y0 - 6], [X(v), y0 + 4]], { w: 2, color: C.ink, seed: seed + 10 + v, passes: 1 }); });
    g.textAlign = 'right'; g.textBaseline = 'middle';
    yticks.forEach(v => { g.fillText(String(v), x0 - 10, Y(v)); ink([[x0 - 4, Y(v)], [x0 + 6, Y(v)]], { w: 2, color: C.ink, seed: seed + 30 + v, passes: 1 }); });
    if (xLabel) hand(xLabel, (x0 + x1) / 2, y0 + 48, 30, -1e6, { dur: 0.01, color: C.navy, seed: seed + 4 });
    if (yLabel) { g.save(); g.translate(x0 - 62, (y0 + y1) / 2); g.rotate(-Math.PI / 2); hand(yLabel, 0, 0, 30, -1e6, { dur: 0.01, color: C.navy, seed: seed + 5 }); g.restore(); }
    // the fitted line, drawn along by prog
    if (line && prog > 0) {
      const pts = []; const n = 24, xe = xmax * clamp(prog);
      for (let i = 0; i <= n; i++) { const v = xe * i / n, yv = line(v); if (yv > ymax * 1.02) break; pts.push([X(v), Y(yv)]); }
      if (pts.length > 1) ink(pts, { w: 4, color: C.red, seed: seed + 6, passes: 1, step: 30, amp: 0.6 });
    }
    // points
    points.forEach((p, i) => {
      const sc = pop(T, pointsT0 + i * 0.12, 0.3, 2);
      if (sc <= 0) return;
      const px = X(p.x), py = Y(p.y);
      g.save(); g.translate(px, py); g.scale(sc, sc);
      paperCircle(0, 0, p.hi ? 13 : 10, p.hi ? C.red : C.blueDeep, { seed: seed + 100 + i, depth: 0.5 });
      g.restore();
      if (p.label) hand(p.label, px + 16, py - 22, 24, -1e6, { dur: 0.01, align: 'left', color: C.ink, seed: seed + 120 + i });
    });
    g.restore();
    const c = Math.cos(rot), sn = Math.sin(rot);
    const toScreen = (lx, ly) => ({ x: x + lx * c - ly * sn, y: y + lx * sn + ly * c });
    return { X: v => toScreen(X(v), 0).x, Y: v => toScreen(0, Y(v)).y, at: (vx, vy) => toScreen(X(vx), Y(vy)) };
  }

  function pencil(pts, { prog = 1, seed = 5, w = 9 } = {}) { ink(pts, { w, color: C.red, seed, prog, passes: 1, step: 14, amp: 1.2 }); }
  function strike(x, y, w, { prog = 1, seed = 7 } = {}) { pencil([[x - w / 2 - 8, y + 4], [x + w / 2 + 8, y - 4]], { prog, seed }); }
  function ring(x, y, rx, ry, { seed = 9, w = 7 } = {}) { inkCircle(x, y, rx, { w, color: C.red, seed, sx: 1, sy: ry / rx, step: 8 }); }

  window.Fair = { C, fitFont, backdrop, floor, table, card, board, graph, pencil, strike, ring, stampWord };
})();
